<template>
  <div class="flex items-center space-x-10">
    <button
      class="border group inline-flex items-center justify-center rounded-full p-4"
      @click="prev"
      :disabled="from === 1"
    >
      <span class="sr-only">{{ t('previous') }}</span>
      <svg
        width="16"
        height="16"
        class="group-hover:shake-left h-4 w-4"
        aria-hidden="true"
        focusable="false"
      >
        <use v-bind="{ 'xlink:href': '#arrow-left' }" />
      </svg>
    </button>

    <div class="flex space-x-1 text-sm">
      <button
        v-if="page !== 1 && !before.includes(1)"
        class="p-2 opacity-20 hover:opacity-100"
        @click="goto(1)"
      >
        <span class="sr-only">{{ t('page') }}</span> 1
      </button>
      <button
        v-for="page in before"
        :key="page"
        class="p-2 opacity-20 hover:opacity-100"
        @click="goto(page)"
      >
        <span class="sr-only">{{ t('page') }}</span> {{ page }}
      </button>
      <span class="p-2 text-base">{{ page }}</span>
      <button
        v-for="page in after"
        :key="page"
        class="p-2 opacity-20 hover:opacity-100"
        @click="goto(page)"
      >
        <span class="sr-only">{{ t('page') }}</span> {{ page }}
      </button>
      <button
        v-if="page !== maxPage && !after.includes(maxPage)"
        class="p-2 opacity-20 hover:opacity-100"
        @click="goto(maxPage)"
      >
        <span class="sr-only">{{ t('page') }}</span> {{ maxPage }}
      </button>
    </div>

    <button
      class="border group inline-flex items-center justify-center rounded-full p-4"
      @click="next"
      :disabled="to === totalCount"
    >
      <span class="sr-only">{{ t('next') }}</span>
      <span class="block rotate-180 transform">
        <svg
          width="16"
          height="16"
          class="group-hover:shake-right h-4 w-4"
          aria-hidden="true"
          focusable="false"
        >
          <use v-bind="{ 'xlink:href': '#arrow-left' }" />
        </svg>
      </span>
    </button>
  </div>
</template>

<i18n>
{
  "en": {
    "previous": "Previous Page",
    "next": "Next Page",
    "page": "Page"
  },
  "de": {
    "previous": "Vorherige Seite",
    "next": "Nächste Seite",
    "page": "Seite"
  }
}
</i18n>
<script lang="ts">
import { SetupContext, computed, defineComponent } from 'vue';
import { pagination } from '../utils/filter';
import { range } from '../utils/utils';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  props: {
    totalCount: {
      type: Number,
      required: true,
    },
  },

  setup(props, context: SetupContext) {
    const from = computed(() => {
      return pagination.skip + 1;
    });

    const to = computed(() => {
      return Math.min(pagination.skip + pagination.pageSize, props.totalCount);
    });

    const page = computed(() => {
      return pagination.skip / pagination.pageSize + 1;
    });

    const maxPage = computed(() => {
      return Math.ceil(props.totalCount / pagination.pageSize);
    });

    const before = computed(() => {
      return range(Math.max(1, page.value - 2), page.value);
    });

    const after = computed(() => {
      return range(page.value + 1, Math.min(maxPage.value + 1, page.value + 3));
    });

    const prev = (): void => {
      if (pagination.skip > 0) {
        context.emit('prev');
      }
    };

    const next = (): void => {
      if (to.value !== props.totalCount) {
        context.emit('next');
      }
    };

    const goto = (number: number): void => {
      context.emit('goto', number);
    };

    return {
      prev,
      next,
      goto,
      from,
      to,
      page,
      maxPage,
      before,
      after,
      ...useI18n(),
    };
  },
});
</script>
